import {
  APP_INITIALIZER,
  ApplicationConfig,
  EnvironmentInjector,
  ErrorHandler,
  importProvidersFrom,
  provideZoneChangeDetection
} from '@angular/core';
import { provideRouter } from '@angular/router';
import { appRoutes } from './app.routes';
import { provideClientHydration } from '@angular/platform-browser';
import { Translations } from './shared/services/translations.service';
import { TranslationService } from '@pc/translation';
import { BrowserAnimationsModule, NoopAnimationsModule } from '@angular/platform-browser/animations';
import { TranslateLoader, TranslateModule, TranslateModuleConfig } from '@ngx-translate/core';
import { from, map, Observable, tap } from 'rxjs';
import { HttpClient, provideHttpClient, withInterceptors } from '@angular/common/http';
import { EnvironmentService } from './environment/environment.service';
import { GtmService } from '@pc/gtm';
import { REST_API_CONFIG, RestApiConfig } from '@pc/services';
import { tokenInterceptor } from './core/interceptors/token.interceptor';
import { GlobalErrorHandler } from './core/interceptors/global-error-handler';

/* Load needed services in a lazy approach after configuration file initialized in an Environment Service.
 If load service directly the configuration file does not have time to load and failed in an api requests  */
export const lazyLoadServices = async (injector: EnvironmentInjector): Promise<void> => {
  const { ProcessTypeDataService } = await import('./shared/services/agent/process-type-data.service');
  const service = injector.get(ProcessTypeDataService);
  service.getProcessTypeAndData();
};

export class TranslationLoader implements TranslateLoader {
  getTranslation(langPath: string): Observable<Record<string, string>> {
    return from(import('./shared/i18n/' + langPath + '.ts')).pipe(map((lang) => lang.default));
  }
}

const appInitializer = (
  environmentService: EnvironmentService,
  gtmService: GtmService,
  injector: EnvironmentInjector
): (() => Observable<unknown>) => {
  return () =>
    environmentService.load().pipe(
      tap(async () => {
        gtmService.initGTM(environmentService.environment.analytics.gtmId);
        await lazyLoadServices(injector);
      })
    );
};

const apiRestConfigFactory = (environmentService: EnvironmentService): RestApiConfig => {
  return {
    apiUrl: environmentService.environment.url.apiUrl
  } as RestApiConfig;
};

export const appConfig: ApplicationConfig = {
  providers: [
    provideHttpClient(withInterceptors([tokenInterceptor])),
    {
      provide: APP_INITIALIZER,
      useFactory: appInitializer,
      deps: [EnvironmentService, GtmService, EnvironmentInjector],
      multi: true
    },
    {
      provide: REST_API_CONFIG,
      useFactory: apiRestConfigFactory,
      deps: [EnvironmentService]
    },
    provideClientHydration(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(appRoutes),
    Translations,
    TranslationService,
    importProvidersFrom(
      NoopAnimationsModule,
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useClass: TranslationLoader,
          deps: [HttpClient]
        }
      } as TranslateModuleConfig | undefined),
      BrowserAnimationsModule
    ),
    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandler
    }
  ]
};
